import { ChangeDetectionStrategy, Component, EventEmitter, HostBinding, HostListener, Input, Output, TemplateRef } from '@angular/core';

import { SportModel } from '@frontend/sports/betting-offer/feature/model';
import { GridConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { Column, GridLayout, GridSorting, Group } from '@frontend/sports/grid/core/feature/model';
import { MarketSwitcherPositionType } from '@frontend/sports/types/configuration';

import { EpcotConfigService, EpcotModule } from '../../common/epcot-config.service';

export interface HeaderGroup {
    column: Column;
    group: Group;
}

@Component({
    selector: 'ms-grid-header',
    templateUrl: 'grid-header.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridHeaderComponent {
    isAccordionCollapsed = false;
    @Input() tracking: string;
    @Input() columns: Column[];
    @Input() sport: SportModel;
    @Input() title?: string;
    @Input() sorting?: GridSorting;
    @Input() isSortingVisible = true;
    @Input() hideTitle = false;
    @Input() sortingTracking?: string;
    @Input() layout?: GridLayout;
    @Input() useColoredIcon = false;
    @Input() hideMarketSwitcher = false;
    @Input() setMarketSwitcherHeight = false;
    @Input() isSportHeaderCollapsed: boolean | undefined;
    @Input() eventsCount: number;
    @Input() showRadioInputInSortSelector?: boolean = false;

    @Input() baseCount: number;
    @Input() offerBadgeTemplate?: TemplateRef<any>;
    @Input() iconTemplate?: TemplateRef<any>;
    @Input() popperAppendTo: string;
    @Input() customClass?: string;
    @Input() eventDetails?: string;
    @Input() gridHeaderOverride?: string;
    @Output() groupChange = new EventEmitter<HeaderGroup>();
    @Output() moreChange = new EventEmitter<boolean>();
    @Output() sortingChange = new EventEmitter<GridSorting>();
    @Output() toggleAccordion = new EventEmitter<boolean>();
    module = EpcotModule.Grid;
    isEpcotEnabled: boolean;

    @HostBinding('class') className = 'grid-header';
    @HostBinding('attr.data-sportname') get sportName() {
        return this.sport?.name;
    }

    @HostBinding('class.sorting')
    get isSorting() {
        return this.showGroupContainer && this.sortingVisible;
    }

    @HostListener('click')
    onClick(): void {
        this.isAccordionCollapsed = !this.isAccordionCollapsed;
        this.toggleAccordion.emit(this.isAccordionCollapsed);
    }

    layoutType = GridLayout;

    constructor(
        public sitecore: Sitecore,
        private epcotConfigService: EpcotConfigService,
        private gridConfig: GridConfig,
    ) {
        this.isEpcotEnabled = this.epcotConfigService.isEnabled();
    }

    get showGroupContainer(): boolean {
        return this.headerInRow || this.sortingVisible || !this.hideMarketSwitcher;
    }

    get sortingVisible(): boolean {
        return this.sorting !== undefined && this.isSortingVisible;
    }

    get headerOnTop(): boolean {
        return !this.hideTitle && !this.title && this.sortingVisible;
    }

    get headerInRow(): boolean {
        return (!this.hideTitle && !this.title && !this.sortingVisible) || this.title != null;
    }

    get iconClass(): string {
        return `sports-${this.sport.id}`;
    }

    get contentClass(): string {
        const baseClass =
            this.layout === GridLayout.SixPack && this.hideMarketSwitcher
                ? `base-count-x${this.baseCount} six-pack market-switcher`
                : this.setMarketSwitcherHeight
                  ? `market-switcher`
                  : this.layout === GridLayout.SixPack
                    ? `base-count-x${this.baseCount} six-pack`
                    : '';
        if (this.customClass) {
            return `${baseClass} ${this.customClass}`;
        }

        return baseClass;
    }

    get showSwitcherBelowSubHeader(): boolean {
        return this.gridConfig.marketSwitcherPosition === MarketSwitcherPositionType.SeparateRow && !this.sortingVisible;
    }

    get addGridGroupContainerClass(): boolean {
        if (!this.hideMarketSwitcher && this.showSwitcherBelowSubHeader) {
            return this.headerInRow;
        }
        return true;
    }

    groupChanged(column: Column, group: Group): void {
        this.groupChange.next({ column, group });
    }

    moreChanged(collapsed: boolean): void {
        this.moreChange.next(collapsed);
    }

    sortingChanged(sortBy: GridSorting): void {
        this.sortingChange.next(sortBy);
    }
}
